import { Shape } from './index.js';

export class ImageRect extends Shape {
	image = null;
	aspectRatio = 'none';
	objectFit = 'fill';
	objectPosition = { x: 0.5, y: 0.5 };

	constructor({ src, x = 0, y = 0, width = 0, height = 0, fillRule = 'color', fill = '', stroke = [0, ''], radius = 0, rotation = 0, shadow = {}, aspectRatio = 'none', objectFit = 'fill', objectPosition = { x: 0.5, y: 0.5 } }) {
		super({ x, y, width, height, fillRule, fill, stroke, radius, rotation, shadow });
		this.src = src;
		this.aspectRatio = aspectRatio;
		this.objectFit = objectFit;
		this.objectPosition = objectPosition;
	}

	async draw(context) {
		await this.#load();

		const { top, right, bottom, left } = this.radius;
		const { width, height } = this.size;
		const { x, y } = this.position;
		const imgWidth = this.image.width;
		const imgHeight = this.image.height;
		let drawWidth = width;
		let drawHeight = height;
		let sx = 0, sy = 0, sWidth = imgWidth, sHeight = imgHeight;

		// Handle objectFit logic
		if (this.objectFit === 'contain') {
			const scale = Math.min(width / imgWidth, height / imgHeight);
			drawWidth = imgWidth * scale;
			drawHeight = imgHeight * scale;
		} else if (this.objectFit === 'cover') {
			const scale = Math.max(width / imgWidth, height / imgHeight);
			drawWidth = width;
			drawHeight = height;
			sWidth = width / scale;
			sHeight = height / scale;
			sx = (imgWidth - sWidth) * this.objectPosition.x;
			sy = (imgHeight - sHeight) * this.objectPosition.y;
		} else if (this.objectFit === 'none') {
			drawWidth = imgWidth;
			drawHeight = imgHeight;
		} else if (this.objectFit === 'fill') {
			drawWidth = width;
			drawHeight = height;
		}

		const dx = (width - drawWidth) * this.objectPosition.x;
		const dy = (height - drawHeight) * this.objectPosition.y;

		context.save();
		context.translate(x, y);
		context.rotate(this.rotation * Math.PI / 180);

		// Apply styles (fill, shadow)
		this.applyStyles(context);

		// Draw the border (stroke) if any
		Object.keys(this.stroke).forEach(side => {
			const strokeSide = this.stroke[side];
			if (strokeSide.size > 0) {
				context.lineWidth = strokeSide.size;
				context.strokeStyle = strokeSide.color;
				context.beginPath();
				context.moveTo(left, 0);
				context.arcTo(0, 0, 0, top, top);
				context.arcTo(0, height, right, height, right);
				context.arcTo(width, height, width, bottom, bottom);
				context.arcTo(width, 0, left, 0, left);
				context.closePath();
				context.stroke(); // Apply stroke here
			}
		});

		// Clip and draw image
		context.beginPath();
		context.moveTo(left, 0);
		context.arcTo(0, 0, 0, top, top);
		context.arcTo(0, height, right, height, right);
		context.arcTo(width, height, width, bottom, bottom);
		context.arcTo(width, 0, left, 0, left);
		context.closePath();
		context.clip(); // Apply clipping path

		// Draw the image within the clipping area
		context.drawImage(this.image, sx, sy, sWidth, sHeight, dx, dy, drawWidth, drawHeight);
		this.beforeRestore(context)
		context.restore();
	}


	async #load() {
		if (this.src instanceof HTMLCanvasElement) {
			this.image = this.src;
			return ;
		}
		if (this.image) return; // Prevent reloading if already loaded

		this.image = new Image();
		this.image.src = this.src; // Use the stored source
		return new Promise((resolve, reject) => {
			this.image.onload = resolve;
			this.image.onerror = reject;
		});
	}
}
